import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb } from "lucide-react";
import { masterPromptCategories, masterPromptTemplates } from "@/data/masterPromptTemplates";

export default function TemplatesPage() {
  const [category, setCategory] = useState("Kaikki");
  const filtered = useMemo(() => (category === "Kaikki" ? masterPromptTemplates : masterPromptTemplates.filter((template) => template.kategoria === category)), [category]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="templates-page">
      <div className="mb-8 max-w-3xl" data-testid="templates-heading-block">
        <p className="text-sm font-extrabold uppercase text-[#D97757]" data-testid="templates-kicker">Promptipohjat</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-[#2C2F2E] sm:text-5xl" data-testid="templates-title">Aloita valmiista pohjasta</h1>
        <p className="mt-4 text-base leading-7 text-[#5C6360]" data-testid="templates-description">Valitse valmis pohja, täytä tärkeimmät tiedot ja kopioi valmis prompti käyttöösi. Aloita lyhytvideon käsikirjoituksesta tai selaa muita pohjia.</p>
      </div>

      <section className="mb-6 rounded-[1.75rem] border border-[#E8E5D9] bg-[#F5F3EC] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:flex sm:items-center sm:justify-between sm:gap-6" data-testid="templates-idea-prompt-card">
        <div className="flex items-start gap-4" data-testid="templates-idea-prompt-content">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#4A6753] text-white" data-testid="templates-idea-prompt-icon"><Lightbulb className="h-5 w-5" aria-hidden="true" /></span>
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="templates-idea-prompt-title">Onko sinulla jo idea?</h2>
            <p className="mt-1 text-sm leading-6 text-[#5C6360]" data-testid="templates-idea-prompt-description">Muuta se valmiiksi työkalukohtaiseksi promptiksi ilman, että sinun tarvitsee valita pohjaa ensin.</p>
          </div>
        </div>
        <Link to="/ideasta-promptiksi" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D97757] px-5 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:mt-0 sm:w-auto" data-testid="templates-idea-prompt-link">
          Muuta se promptiksi <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </section>

      <div className="mb-6 flex gap-2 overflow-x-auto pb-2" data-testid="template-category-tabs">
        {["Kaikki", ...masterPromptCategories].map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setCategory(item)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-colors ${category === item ? "bg-[#4A6753] text-white" : "bg-white text-[#3F4542] ring-1 ring-[#E8E5D9] hover:bg-[#F5F3EC]"}`}
            data-testid={`template-category-button-${item.toLowerCase().replaceAll(" / ", "-").replaceAll(" ", "-")}`}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="template-card-grid">
        {filtered.map((template) => (
          <article key={template.id} className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`template-card-${template.id}`}>
            <p className="text-xs font-extrabold uppercase text-[#D97757]" data-testid={`template-category-${template.id}`}>{template.kategoria}</p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-[#2C2F2E]" data-testid={`template-title-${template.id}`}>{template.nimi}</h2>
            <p className="mt-3 text-sm leading-7 text-[#5C6360]" data-testid={`template-description-${template.id}`}>{template.lyhytKuvaus}</p>
            <div className="mt-4 rounded-[1.25rem] bg-[#F5F3EC] p-4" data-testid={`template-meta-${template.id}`}>
              <p className="text-sm font-bold text-[#2C2F2E]" data-testid={`template-purpose-title-${template.id}`}>Mihin tämä auttaa?</p>
              <p className="mt-1 text-sm leading-7 text-[#5C6360]" data-testid={`template-purpose-${template.id}`}>{template.kayttotarkoitus}</p>
              <p className="mt-4 text-sm font-bold text-[#2C2F2E]" data-testid={`template-fields-title-${template.id}`}>Täytettävät tiedot</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid={`template-fields-${template.id}`}>
                {template.lomakekentat.map((field) => (
                  <span key={field.id} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#4A6753] ring-1 ring-[#E8E5D9]" data-testid={`template-field-${template.id}-${field.id}`}>{field.label}</span>
                ))}
              </div>
              <p className="mt-4 text-sm font-bold text-[#2C2F2E]" data-testid={`template-tags-title-${template.id}`}>Avainsanat</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid={`template-tags-${template.id}`}>
                {template.tagit.map((tag) => (
                  <span key={tag} className="rounded-full bg-[#4A6753]/10 px-3 py-1 text-xs font-bold text-[#4A6753]" data-testid={`template-tag-${template.id}-${tag}`}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2" data-testid={`template-actions-${template.id}`}>
              <Link to={`/pohjat/${template.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#D97757] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" data-testid={`template-open-builder-${template.id}`}>
                Täytä tiedot <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}